import {Segment} from 'platform/components';
import {Show, Space} from 'platform/foundation';
import {useTheme} from 'styled-components';
import {match} from 'ts-pattern';

import {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {Link, useLocation} from 'react-router-dom';

import {useGetUserStatsQuery} from '@dms/api/customer';
import {selectActiveBranchId} from '@dms/api/features';
import featureFlags from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {sourcingRoutes} from '@dms/routes';

import {suffixTestId, TestIdProps, useNavigate} from 'shared/web';

import {useSourcingPage} from '../../hooks/useSourcingPage';
import {useSourcingType, SourcingType} from '../../hooks/useSourcingType';
import {SourcingPage} from '../../types/SourcingPage';
import {ControlSectionWrapper} from './components/ControlSectionWrapper';
import {Counter} from './components/Counter';
import {HeaderSettings} from './components/HeaderSettings';
import {Menu} from './components/Menu';
import {OneLine} from './components/OneLine';
import {StyledMenuItem} from './components/StyledMenuItem';
import {TitleSection} from './components/TitleSection';

export function Navigation(props: TestIdProps) {
  const location = useLocation();
  const pageRoute = location.pathname;
  const theme = useTheme();

  const activeBranchId = useSelector(selectActiveBranchId);
  const navigate = useNavigate();
  const currentPage = useSourcingPage();
  const sourcingType = useSourcingType();

  const {data: userStats} = useGetUserStatsQuery(undefined, {
    skip: !activeBranchId,
  });

  const {toBuyCount, favouriteCount, hiddenCount, comparisonCount, carvagoBuyCount} = useMemo(
    () =>
      match(sourcingType)
        .with(SourcingType.CLASSIFIEDS, () => userStats?.userStats)
        .with(SourcingType.AUCTIONS, () => userStats?.userAuctionStats)
        .exhaustive() ?? {
        toBuyCount: 0,
        favouriteCount: 0,
        hiddenCount: 0,
        comparisonCount: 0,
        carvagoBuyCount: 0,
      },
    [userStats, sourcingType]
  );

  const getNewPathname = (source: SourcingType, pageType = SourcingPage.DEFAULT) =>
    match<[SourcingType, SourcingPage], string>([source, pageType])
      .with(
        [SourcingType.CLASSIFIEDS, SourcingPage.FAVORITES_PAGE],
        () => sourcingRoutes.classifieds_userFavourite
      )
      .with(
        [SourcingType.CLASSIFIEDS, SourcingPage.CART_PAGE],
        () => sourcingRoutes.classifieds_userToBuy
      )
      .with(
        [SourcingType.CLASSIFIEDS, SourcingPage.COMPARISON_PAGE],
        () => sourcingRoutes.classifieds_comparison
      )
      .with(
        [SourcingType.CLASSIFIEDS, SourcingPage.HIDDEN_PAGE],
        () => sourcingRoutes.classifieds_userHidden
      )
      .with(
        [SourcingType.CLASSIFIEDS, SourcingPage.CARVAGO_BUY_PAGE],
        () => sourcingRoutes.classifieds_userCarvagoBuy
      )
      .with([SourcingType.CLASSIFIEDS, SourcingPage.DEFAULT], () => sourcingRoutes.classifieds)
      .with(
        [SourcingType.AUCTIONS, SourcingPage.FAVORITES_PAGE],
        () => sourcingRoutes.auctions_userFavourite
      )
      .with(
        [SourcingType.AUCTIONS, SourcingPage.CART_PAGE],
        () => sourcingRoutes.auctions_userToBuy
      )
      .with(
        [SourcingType.AUCTIONS, SourcingPage.COMPARISON_PAGE],
        () => sourcingRoutes.auctions_comparison
      )
      .with(
        [SourcingType.AUCTIONS, SourcingPage.HIDDEN_PAGE],
        () => sourcingRoutes.auctions_userHidden
      )
      .with([SourcingType.AUCTIONS, SourcingPage.CARVAGO_BUY_PAGE], () => sourcingRoutes.auctions)
      .with([SourcingType.AUCTIONS, SourcingPage.DEFAULT], () => sourcingRoutes.auctions)
      .exhaustive();

  return (
    <ControlSectionWrapper>
      <TitleSection data-testid={suffixTestId('titleSection', props)} />
      <Show whenFeatureEnabled={featureFlags.SOURCING_AUCTIONS}>
        <Segment<SourcingType>
          value={sourcingType}
          onChange={(value) => navigate(getNewPathname(value, currentPage))}
          options={[
            {value: SourcingType.CLASSIFIEDS, label: i18n.t('page.sourcing.labels.classifieds')},
            {value: SourcingType.AUCTIONS, label: i18n.t('page.sourcing.labels.auctions')},
          ]}
          data-testid={suffixTestId('offersDropdown', props)}
        />
        <Space horizontal={4} />
      </Show>
      <Menu data-testid={suffixTestId('menu', props)}>
        <StyledMenuItem isActive={pageRoute === getNewPathname(sourcingType, SourcingPage.DEFAULT)}>
          <Link
            to={getNewPathname(sourcingType, SourcingPage.DEFAULT)}
            data-testid={suffixTestId('userListPage', props)}
          >
            {i18n.t('general.actions.allOffers')}
          </Link>
        </StyledMenuItem>
        <StyledMenuItem
          isActive={pageRoute === getNewPathname(sourcingType, SourcingPage.FAVORITES_PAGE)}
        >
          <Link
            to={getNewPathname(sourcingType, SourcingPage.FAVORITES_PAGE)}
            data-testid={suffixTestId('favoritesPage', props)}
          >
            <OneLine>
              {i18n.t('general.actions.favourites')}
              <Counter count={favouriteCount} color={theme.colors.palettes.orange[60][100]} />
            </OneLine>
          </Link>
        </StyledMenuItem>
        <StyledMenuItem
          isActive={pageRoute === getNewPathname(sourcingType, SourcingPage.CART_PAGE)}
        >
          <Link
            to={getNewPathname(sourcingType, SourcingPage.CART_PAGE)}
            data-testid={suffixTestId('cartPage', props)}
          >
            <OneLine>
              {i18n.t('entity.vehicle.labels.toBuyPage')}
              <Counter count={toBuyCount} color={theme.colors.palettes.blue[60][100]} />
            </OneLine>
          </Link>
        </StyledMenuItem>
        <StyledMenuItem
          isActive={pageRoute === getNewPathname(sourcingType, SourcingPage.COMPARISON_PAGE)}
        >
          <Link
            to={getNewPathname(sourcingType, SourcingPage.COMPARISON_PAGE)}
            data-testid={suffixTestId('comparisonPage', props)}
          >
            <OneLine>
              {i18n.t('entity.vehicle.labels.comparisonPage')}
              <Counter count={comparisonCount} color={theme.colors.palettes.purple[60][100]} />
            </OneLine>
          </Link>
        </StyledMenuItem>
        <StyledMenuItem
          isActive={pageRoute === getNewPathname(sourcingType, SourcingPage.HIDDEN_PAGE)}
        >
          <Link
            to={getNewPathname(sourcingType, SourcingPage.HIDDEN_PAGE)}
            data-testid={suffixTestId('hiddenPage', props)}
          >
            <OneLine>
              {i18n.t('entity.vehicle.labels.hiddenPage')}
              <Counter count={hiddenCount} color={theme.colors.palettes.neutral[200][100]} />
            </OneLine>
          </Link>
        </StyledMenuItem>
        <Show
          when={sourcingType === SourcingType.CLASSIFIEDS}
          whenFeatureEnabled={featureFlags.SOURCING_CARVAGO_BUY}
        >
          <StyledMenuItem
            isActive={pageRoute === getNewPathname(sourcingType, SourcingPage.CARVAGO_BUY_PAGE)}
          >
            <Link
              to={getNewPathname(sourcingType, SourcingPage.CARVAGO_BUY_PAGE)}
              data-testid={suffixTestId('carvagoBuyPage', props)}
            >
              <OneLine>
                {i18n.t('entity.vehicle.labels.carvagoBuyPage')}
                <Counter count={carvagoBuyCount} color={theme.colors.palettes.teal[60][100]} />
              </OneLine>
            </Link>
          </StyledMenuItem>
        </Show>
      </Menu>
      <HeaderSettings />
    </ControlSectionWrapper>
  );
}
